import React, { useState } from 'react';
import { X, PlusCircle, Pencil, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CustomField {
  id: string;
  name: string;
  field_type: string;
  options?: string[];
}

interface CustomFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  existingFields: CustomField[];
  onFieldsUpdate: (fields: CustomField[]) => void;
}

export function CustomFieldsModal({
  isOpen,
  onClose,
  projectId,
  existingFields,
  onFieldsUpdate
}: CustomFieldsModalProps) {
  const [fields, setFields] = useState<CustomField[]>(existingFields);
  const [isAddingField, setIsAddingField] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newField, setNewField] = useState({
    name: '',
    field_type: 'text',
    options: ''
  });

  if (!isOpen) return null;

  async function handleAddField(e: React.FormEvent) {
    e.preventDefault();
    try {
      const fieldData = {
        project_id: projectId,
        name: newField.name,
        field_type: newField.field_type,
        options: ['single_select', 'multi_select'].includes(newField.field_type) 
          ? newField.options.split(',').map(opt => opt.trim())
          : null
      };

      const { data, error } = await supabase
        .from('custom_fields')
        .insert(fieldData)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        const updatedFields = [...fields, data];
        setFields(updatedFields);
        onFieldsUpdate(updatedFields);
        setIsAddingField(false);
        setNewField({ name: '', field_type: 'text', options: '' });
      }
    } catch (error: any) {
      setError(error.message);
    }
  }

  async function handleUpdateField(field: CustomField) {
    try {
      const fieldData = {
        name: field.name,
        field_type: field.field_type,
        options: ['single_select', 'multi_select'].includes(field.field_type) 
          ? field.options
          : null
      };

      const { error } = await supabase
        .from('custom_fields')
        .update(fieldData)
        .eq('id', field.id);

      if (error) throw error;
      
      const updatedFields = fields.map(f => f.id === field.id ? { ...f, ...fieldData } : f);
      setFields(updatedFields);
      onFieldsUpdate(updatedFields);
      setEditingField(null);
    } catch (error: any) {
      setError(error.message);
    }
  }

  const showOptionsInput = ['single_select', 'multi_select'].includes(newField.field_type);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div>
            <h2 className="text-lg font-medium leading-6 text-brand-teal mb-4">
              Manage Custom Fields
            </h2>

            <button
              onClick={() => setIsAddingField(true)}
              className="mb-4 inline-flex items-center text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              Add Field
            </button>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isAddingField && (
              <div className="mb-4 space-y-4 bg-gray-50 p-4 rounded-lg">
                <input
                  type="text"
                  placeholder="Field Name"
                  value={newField.name}
                  onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                />

                <select
                  value={newField.field_type}
                  onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Yes/No</option>
                  <option value="date">Date</option>
                  <option value="single_select">Single Select</option>
                  <option value="multi_select">Multi Select</option>
                </select>

                {showOptionsInput && (
                  <input
                    type="text"
                    placeholder="Options (comma-separated)"
                    value={newField.options}
                    onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                  />
                )}

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setIsAddingField(false)}
                    className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddField}
                    disabled={!newField.name}
                    className="px-3 py-2 text-sm text-white bg-brand-orange hover:bg-brand-orange-light rounded-lg disabled:opacity-50"
                  >
                    Add Field
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {fields.map((field) => (
                <div key={field.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  {editingField === field.id ? (
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => setFields(fields.map(f => 
                          f.id === field.id ? { ...f, name: e.target.value } : f
                        ))}
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                      />
                      {['single_select', 'multi_select'].includes(field.field_type) && (
                        <input
                          type="text"
                          value={field.options?.join(', ') || ''}
                          onChange={(e) => setFields(fields.map(f => 
                            f.id === field.id ? { ...f, options: e.target.value.split(',').map(opt => opt.trim()) } : f
                          ))}
                          placeholder="Options (comma-separated)"
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                        />
                      )}
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleUpdateField(field)}
                          className="text-green-600 hover:text-green-900 transition-colors"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingField(null)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <div className="font-medium text-gray-900">{field.name}</div>
                        <div className="text-sm text-gray-500">{field.field_type}</div>
                        {field.options && (
                          <div className="text-sm text-gray-500">
                            Options: {field.options.join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setEditingField(field.id)}
                        className="text-brand-orange hover:text-brand-orange-light transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}